import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from './supabase';
import { Field, Input, Select, Textarea, SaveMessage, Card, CardHeader, SubmitBtn, EmptyState, Badge } from './Shared';

const money = n => Number(n || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
const fmtDate = d => d ? new Date(d + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';
const monthLabel = d => new Date(d + 'T00:00:00').toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
const quarterLabel = d => { const x = new Date(d + 'T00:00:00'); return 'Q' + (Math.floor(x.getMonth() / 3) + 1) + ' ' + x.getFullYear(); };
const todayStr = () => new Date().toLocaleDateString('en-CA');

export const REVIEW_ENTITIES = [
  { value: 'ao', label: 'AO Group Holdings (royalty ledger)' },
  { value: 'national', label: 'REDLINE Fire Watch LLC' },
  { value: 'academy', label: 'Fire Watch Academy LLC' },
  { value: 'technologies', label: 'Fire Watch Technologies LLC' },
  { value: 'newjersey', label: 'Fire Watch New Jersey LLC' }
];
const ENTITY_NAME = Object.fromEntries(REVIEW_ENTITIES.map(e => [e.value, e.label]));
const KINDS = [
  { value: 'admin_review', label: 'Admin review — ask Jacob to look into something' },
  { value: 'accountant_review', label: 'Independent CPA review (Term Sheet §3.4 / §12)' }
];
const STATUS_LABEL = { open: 'Open', in_review: 'In review', scheduled: 'Scheduled', completed: 'Completed', declined: 'Declined' };
const STATUS_VARIANT = { open: 'blue', in_review: 'amber', scheduled: 'purple', completed: 'green', declined: 'gray' };

// ------------------------------------------------------------------ participant portal
export default function ParticipationPortal({ profile }) {
  const [summary, setSummary] = useState(null);
  const [quarters, setQuarters] = useState([]);
  const [accruals, setAccruals] = useState([]);
  const [payments, setPayments] = useState([]);
  const [gross, setGross] = useState([]);
  const [loadError, setLoadError] = useState('');

  const load = useCallback(async () => {
    const q = await Promise.all([
      supabase.from('participation_summary').select('*').maybeSingle(),
      supabase.from('participation_quarterly').select('*').order('q', { ascending: false }),
      supabase.from('participation_accruals').select('*').order('received_date', { ascending: false }),
      supabase.from('participation_payments').select('*').order('paid_date', { ascending: false }),
      supabase.from('participant_gross_revenue').select('*').order('period', { ascending: false })
    ]);
    const err = q.find(r => r.error);
    setLoadError(err ? err.error.message : '');
    setSummary(q[0].data); setQuarters(q[1].data || []); setAccruals(q[2].data || []); setPayments(q[3].data || []); setGross(q[4].data || []);
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <div>
      <div style={{ fontSize: '13px', color: '#64748B', marginBottom: '16px', lineHeight: '1.6' }}>
        Your Royalty Participation Right (Term Sheet §3). It accrues as AO collects royalties; payment timing is at the Manager's discretion
        and the balance carries no interest. This is a read-only view of the Participation Ledger.
      </div>
      {loadError && <Card><div style={{ color: '#B91C1C', fontSize: '13px' }}>Could not load: {loadError}</div></Card>}

      <div className="metrics-grid">
        <div className="metric-card"><div className="metric-label">Total accrued</div><div className="metric-value blue">{money(summary && summary.total_accrued)}</div></div>
        <div className="metric-card"><div className="metric-label">Paid to you</div><div className="metric-value green">{money(summary && summary.total_paid)}</div></div>
        <div className="metric-card"><div className="metric-label">Outstanding balance</div><div className="metric-value red">{money(summary && summary.outstanding)}</div></div>
      </div>

      <Card>
        <CardHeader title="Quarterly statement" hint="§3.4 · issued within 30 days of quarter end" />
        {!quarters.length ? <EmptyState text="No royalties have been collected yet." /> : (
          <table className="data-table">
            <thead><tr><th>Quarter</th><th style={{ textAlign: 'right' }}>Royalties received: REDLINE</th><th style={{ textAlign: 'right' }}>Academy</th><th style={{ textAlign: 'right' }}>Technologies</th>
              <th style={{ textAlign: 'right' }}>Manager comp paid at AO</th><th style={{ textAlign: 'right' }}>Your accrual</th><th style={{ textAlign: 'right' }}>Paid to you</th><th style={{ textAlign: 'right' }}>Running balance</th></tr></thead>
            <tbody>{quarters.map(r => (
              <tr key={r.q}><td>{quarterLabel(r.q)}<div style={{ fontSize: '11px', color: '#94A3B8' }}>due {fmtDate(r.statement_due)}</div></td>
                <td style={{ textAlign: 'right' }}>{money(r.recv_redline)}</td><td style={{ textAlign: 'right' }}>{money(r.recv_academy)}</td><td style={{ textAlign: 'right' }}>{money(r.recv_technologies)}</td>
                <td style={{ textAlign: 'right' }}>{money(r.manager_comp_paid)}</td><td style={{ textAlign: 'right' }}>{money(r.accrual)}</td>
                <td style={{ textAlign: 'right' }}>{money(r.paid_to_wesley)}</td><td style={{ textAlign: 'right' }}>{money(r.running_balance)}</td></tr>
            ))}</tbody>
          </table>
        )}
        <div style={{ fontSize: '11px', color: '#94A3B8', marginTop: '8px' }}>Accrual is calculated on royalties actually received, before any manager compensation deduction.</div>
      </Card>

      <Card>
        <CardHeader title="Gross revenue and AO royalty income" hint="Entities you hold no participation in show gross figures only" />
        {!gross.length ? <EmptyState text="Nothing reported yet." /> : (
          <table className="data-table"><thead><tr><th>Month</th><th>Line</th><th style={{ textAlign: 'right' }}>Amount</th></tr></thead>
            <tbody>{gross.map((g, i) => (<tr key={i}><td>{monthLabel(g.period)}</td><td>{g.label}</td><td style={{ textAlign: 'right' }}>{money(g.amount)}</td></tr>))}</tbody></table>
        )}
      </Card>

      <Card>
        <CardHeader title="Accrual detail" hint="One line per royalty payment AO received" />
        {!accruals.length ? <EmptyState text="No accruals yet." /> : (
          <table className="data-table"><thead><tr><th>Received</th><th>Invoice</th><th style={{ textAlign: 'right' }}>Royalty received</th><th style={{ textAlign: 'right' }}>Your share</th></tr></thead>
            <tbody>{accruals.map((a, i) => (<tr key={i}><td>{fmtDate(a.received_date)}</td><td>{a.invoice_no}</td><td style={{ textAlign: 'right' }}>{money(a.royalty_received)}</td><td style={{ textAlign: 'right' }}>{money(a.accrual)}</td></tr>))}</tbody></table>
        )}
        {payments.length > 0 && (<>
          <div style={{ fontWeight: 600, fontSize: '13px', margin: '14px 0 6px' }}>Payments to you</div>
          <table className="data-table"><thead><tr><th>Date</th><th style={{ textAlign: 'right' }}>Amount</th><th>Note</th></tr></thead>
            <tbody>{payments.map(p => (<tr key={p.id}><td>{fmtDate(p.paid_date)}</td><td style={{ textAlign: 'right' }}>{money(p.amount)}</td><td>{p.note || '—'}</td></tr>))}</tbody></table>
        </>)}
      </Card>

      <ReviewRequests profile={profile} mode="participant" />
    </div>
  );
}

// ------------------------------------------------------------------ review requests (both sides)
export function ReviewRequests({ profile, mode }) {
  const isAdmin = mode === 'admin';
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(null);
  const [form, setForm] = useState({ kind: 'admin_review', entity: 'ao', period_from: '', period_to: '', description: '' });
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState({ show: false, text: '', error: false });
  const flash = (text, error = false) => { setMsg({ show: true, text, error }); if (!error) setTimeout(() => setMsg({ show: false }), 5000); };

  const load = useCallback(async () => {
    const { data } = await supabase.from('review_requests').select('*').order('notice_at', { ascending: false });
    setRows(data || []);
  }, []);
  useEffect(() => { load(); }, [load]);

  async function submit() {
    if (!form.description.trim()) return flash('Describe what you want reviewed.', true);
    setSaving(true);
    const { error } = await supabase.from('review_requests').insert({
      requested_by: profile.id, kind: form.kind, entity: form.entity,
      period_from: form.period_from || null, period_to: form.period_to || null, description: form.description.trim()
    });
    setSaving(false);
    if (error) {
      return flash(error.message.includes('review_one_accountant_per_year')
        ? 'An independent CPA review for that entity has already been requested this calendar year.' : error.message, true);
    }
    flash(form.kind === 'accountant_review' ? 'Request sent. This is your written notice; the 15-day notice period starts now.' : 'Request sent.');
    setForm({ kind: 'admin_review', entity: 'ao', period_from: '', period_to: '', description: '' });
    load();
  }

  return (
    <div>
      {!isAdmin && (
        <Card>
          <CardHeader title="Request a review" hint="Goes straight to Jacob" />
          <div style={{ display: 'grid', gap: '10px' }}>
            <Field label="Type"><Select value={form.kind} onChange={e => setForm(f => ({ ...f, kind: e.target.value }))} options={KINDS} /></Field>
            <Field label="What should be reviewed"><Select value={form.entity} onChange={e => setForm(f => ({ ...f, entity: e.target.value }))} options={REVIEW_ENTITIES} /></Field>
            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
              <div style={{ flex: 1, minWidth: '150px' }}><Field label="From (optional)"><Input type="date" value={form.period_from} onChange={e => setForm(f => ({ ...f, period_from: e.target.value }))} /></Field></div>
              <div style={{ flex: 1, minWidth: '150px' }}><Field label="To (optional)"><Input type="date" value={form.period_to} onChange={e => setForm(f => ({ ...f, period_to: e.target.value }))} /></Field></div>
            </div>
            <Field label="Describe the concern or the scope of the review"><Textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder="What looks wrong or what do you want verified?" /></Field>
            {form.kind === 'accountant_review' && (
              <div style={{ fontSize: '12px', color: '#64748B', lineHeight: '1.6', background: '#F8FAFC', padding: '10px', borderRadius: '8px' }}>
                Per the Term Sheet: a mutually agreed independent CPA reviews the books on 15 days' written notice, once per calendar year per entity.
                Cost is shared equally unless the review finds a discrepancy over 5%, in which case the entity pays and corrects the ledger.
                Submitting this request is your written notice.
              </div>
            )}
            <div><SubmitBtn onClick={submit} loading={saving}>Send request</SubmitBtn></div>
            <SaveMessage {...msg} />
          </div>
        </Card>
      )}
      {isAdmin && <SaveMessage {...msg} />}

      <Card>
        <CardHeader title={isAdmin ? 'Review requests' : 'Your requests'} hint={rows.length + ' total'} />
        {!rows.length ? <EmptyState text="No review requests." /> : rows.map(r => (
          <RequestRow key={r.id} r={r} isAdmin={isAdmin} profile={profile} expanded={open === r.id} onToggle={() => setOpen(open === r.id ? null : r.id)} reload={load} flash={flash} />
        ))}
      </Card>
    </div>
  );
}

function RequestRow({ r, isAdmin, profile, expanded, onToggle, reload, flash }) {
  const [thread, setThread] = useState([]);
  const [reply, setReply] = useState('');
  const [edit, setEdit] = useState({ status: r.status, accountant_name: r.accountant_name || '', resolution: r.resolution || '' });
  const earliest = new Date(r.notice_at); earliest.setDate(earliest.getDate() + 15);

  useEffect(() => {
    if (!expanded) return;
    supabase.from('review_messages').select('*').eq('request_id', r.id).order('created_at').then(({ data }) => setThread(data || []));
  }, [expanded, r.id]);

  async function send() {
    if (!reply.trim()) return;
    const { error } = await supabase.from('review_messages').insert({ request_id: r.id, author: profile.id, body: reply.trim() });
    if (error) return flash(error.message, true);
    setReply('');
    const { data } = await supabase.from('review_messages').select('*').eq('request_id', r.id).order('created_at');
    setThread(data || []);
  }
  async function saveWorkflow() {
    const done = ['completed', 'declined'].includes(edit.status);
    const { error } = await supabase.from('review_requests').update({
      status: edit.status, accountant_name: edit.accountant_name.trim() || null, resolution: edit.resolution.trim() || null,
      updated_at: new Date().toISOString(), closed_at: done ? new Date().toISOString() : null
    }).eq('id', r.id);
    if (error) return flash(error.message, true);
    flash('Saved.'); reload();
  }

  return (
    <div className="list-row" style={{ display: 'block' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: '10px', cursor: 'pointer' }} onClick={onToggle}>
        <div>
          <div className="list-row-name">{r.kind === 'accountant_review' ? 'CPA review' : 'Admin review'} · {ENTITY_NAME[r.entity]}</div>
          <div className="list-row-meta">{new Date(r.notice_at).toLocaleDateString('en-US')} · {r.description.length > 110 ? r.description.slice(0, 110) + '…' : r.description}</div>
        </div>
        <Badge variant={STATUS_VARIANT[r.status]}>{STATUS_LABEL[r.status]}</Badge>
      </div>
      {expanded && (
        <div style={{ marginTop: '10px', fontSize: '13px' }}>
          <div style={{ marginBottom: '8px', whiteSpace: 'pre-wrap' }}>{r.description}</div>
          {(r.period_from || r.period_to) && <div className="list-row-meta">Period: {fmtDate(r.period_from)} – {fmtDate(r.period_to)}</div>}
          {r.kind === 'accountant_review' && <div className="list-row-meta">Written notice given {new Date(r.notice_at).toLocaleDateString('en-US')} · earliest review start {earliest.toLocaleDateString('en-US')}{r.accountant_name ? ' · CPA: ' + r.accountant_name : ''}</div>}
          {r.resolution && <div style={{ marginTop: '6px' }}><strong>Resolution:</strong> {r.resolution}</div>}

          {isAdmin && (
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', margin: '10px 0' }}>
              <div style={{ width: '160px' }}><Select value={edit.status} onChange={e => setEdit(x => ({ ...x, status: e.target.value }))} options={Object.keys(STATUS_LABEL).map(k => ({ value: k, label: STATUS_LABEL[k] }))} /></div>
              {r.kind === 'accountant_review' && <div style={{ width: '200px' }}><Input value={edit.accountant_name} onChange={e => setEdit(x => ({ ...x, accountant_name: e.target.value }))} placeholder="Agreed CPA / firm" /></div>}
              <div style={{ flex: 1, minWidth: '200px' }}><Input value={edit.resolution} onChange={e => setEdit(x => ({ ...x, resolution: e.target.value }))} placeholder="Resolution / outcome" /></div>
              <button className="btn btn-primary btn-sm" onClick={saveWorkflow}>Save</button>
            </div>
          )}

          <div style={{ background: '#F8FAFC', borderRadius: '8px', padding: '10px', margin: '8px 0' }}>
            {!thread.length ? <div style={{ fontSize: '12px', color: '#94A3B8' }}>No messages yet.</div> : thread.map(m => (
              <div key={m.id} style={{ marginBottom: '8px' }}>
                <div style={{ fontSize: '11px', color: '#94A3B8' }}>{m.author === r.requested_by ? 'Requester' : 'Admin'} · {new Date(m.created_at).toLocaleString('en-US')}</div>
                <div style={{ whiteSpace: 'pre-wrap' }}>{m.body}</div>
              </div>
            ))}
            <div style={{ display: 'flex', gap: '8px', marginTop: '6px' }}>
              <input className="field-input" value={reply} onChange={e => setReply(e.target.value)} placeholder="Add a message (messages cannot be edited or deleted)" />
              <button className="btn btn-secondary btn-sm" onClick={send}>Send</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
